import { Card, CardBody, CardHeader, Progress, Select, SelectItem, Skeleton } from '@heroui/react'
import { AlertTriangle, TrendingUp } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { useIssuesAnalytics, useIssuesTimeSeries } from './utils'

const SEVERITY_COLORS = {
  low: '#10B981', // green-500
  medium: '#F59E0B', // amber-500
  high: '#EF4444', // red-500
}

function formatDate(timestamp, interval) {
  const date = new Date(timestamp)
  const options = { month: 'short', day: 'numeric' }

  if (interval === 'hourly') {
    return new Intl.DateTimeFormat('en-US', {
      ...options,
      hour: 'numeric',
      hour12: true,
    }).format(date)
  }

  if (interval === 'weekly') {
    return new Intl.DateTimeFormat('en-US', options).format(date)
  }

  return new Intl.DateTimeFormat('en-US', options).format(date)
}

function IssuesSummary({ data, isLoading }) {
  if (isLoading) {
    return <Skeleton className="h-32 w-full rounded-none" />
  }

  if (!data?.summary) {
    return null
  }

  const { summary } = data
  const total = summary.total_issues || 0

  if (total === 0) {
    return (
      <Card classNames={{
        base: 'border-2 border-gray-200 shadow-none rounded-none',
      }}
      >
        <CardBody className="flex h-32 items-center justify-center">
          <h1 className={`
            text-xl font-semibold tracking-tighter text-gray-400
          `}
          >
            No issues detected yet.
          </h1>
        </CardBody>
      </Card>
    )
  }

  const severityData = [
    { name: 'Low', value: summary.low_severity_count || 0, color: SEVERITY_COLORS.low },
    { name: 'Medium', value: summary.medium_severity_count || 0, color: SEVERITY_COLORS.medium },
    { name: 'High', value: summary.high_severity_count || 0, color: SEVERITY_COLORS.high },
  ].filter(d => d.value > 0)

  return (
    <Card classNames={{
      base: 'border-2 border-gray-200 shadow-none rounded-none',
    }}
    >
      <CardHeader className="flex items-center justify-between border-b border-gray-200">
        <h4 className="font-taurus text-lg font-semibold">Issues Summary</h4>
        <AlertTriangle size={20} className="text-gray-400" />
      </CardHeader>
      <CardBody className="grid grid-cols-3 gap-4">
        <div>
          <p className="text-xs text-gray-500 uppercase">Total Issues</p>
          <p className="font-taurus text-2xl font-bold">{total}</p>
        </div>
        <div>
          <p className="text-xs text-gray-500 uppercase">Affected Calls</p>
          <p className="font-taurus text-2xl font-bold">{summary.total_calls_with_issues || 0}</p>
        </div>
        <div className="col-span-1">
          <p className="mb-2 text-xs text-gray-500 uppercase">By Severity</p>
          <div className="flex flex-col gap-1">
            {severityData.map(severity => (
              <div key={severity.name} className="flex items-center gap-2">
                <div
                  className="h-3 w-3 rounded-full"
                  style={{ backgroundColor: severity.color }}
                />
                <span className="font-mono text-xs">{severity.name}:</span>
                <span className="font-mono text-xs font-medium">{severity.value}</span>
              </div>
            ))}
          </div>
        </div>
      </CardBody>
    </Card>
  )
}

function IssuesByType({ data, isLoading }) {
  if (isLoading) {
    return <Skeleton className="h-64 w-full rounded-none" />
  }

  if (!data?.issues_by_type?.length) {
    return null
  }

  const topIssues = data.issues_by_type.slice(0, 10)

  return (
    <Card classNames={{
      base: 'border-2 border-gray-200 shadow-none rounded-none',
    }}
    >
      <CardHeader className="border-b border-gray-200">
        <h4 className="font-taurus text-lg font-semibold">Top Issue Types</h4>
      </CardHeader>
      <CardBody>
        <div className="flex flex-col gap-3">
          {topIssues.map((issue, index) => {
            const total = issue.count
            const percentages = {
              low: (issue.low_count / total * 100).toFixed(0),
              medium: (issue.medium_count / total * 100).toFixed(0),
              high: (issue.high_count / total * 100).toFixed(0),
            }

            return (
              <div key={issue.issue_type} className="border-b border-gray-100 pb-3 last:border-0">
                <div className="mb-2 flex items-center justify-between">
                  <span className="font-mono text-sm font-medium">
                    {issue.issue_type.replace(/_/g, ' ')}
                  </span>
                  <span className="font-mono text-xs text-gray-500">
                    {issue.count}
                    {' '}
                    issues in
                    {' '}
                    {issue.affected_calls}
                    {' '}
                    calls
                  </span>
                </div>
                <div className="flex h-2 w-full overflow-hidden rounded-sm bg-gray-100">
                  {issue.low_count > 0 && (
                    <div
                      className="bg-green-500"
                      style={{ width: `${percentages.low}%` }}
                      title={`Low: ${issue.low_count}`}
                    />
                  )}
                  {issue.medium_count > 0 && (
                    <div
                      className="bg-amber-500"
                      style={{ width: `${percentages.medium}%` }}
                      title={`Medium: ${issue.medium_count}`}
                    />
                  )}
                  {issue.high_count > 0 && (
                    <div
                      className="bg-red-500"
                      style={{ width: `${percentages.high}%` }}
                      title={`High: ${issue.high_count}`}
                    />
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </CardBody>
    </Card>
  )
}

function IssuesTimeline({ schemaId, dateRange }) {
  const [timeInterval, setTimeInterval] = useState('daily')
  const { data, isLoading } = useIssuesTimeSeries(schemaId, dateRange, timeInterval)

  const chartData = useMemo(() => {
    if (!data?.time_series?.length)
      return []

    return data.time_series.map((point) => ({
      timestamp: point.timestamp,
      formattedDate: formatDate(point.timestamp, timeInterval),
      ...point,
    }))
  }, [data, timeInterval])

  if (isLoading) {
    return (
      <Card classNames={{
        base: 'w-full border-2 border-gray-200 shadow-none rounded-none',
      }}
      >
        <CardBody>
          <Skeleton className="h-64 w-full rounded-none" />
        </CardBody>
      </Card>
    )
  }

  if (!chartData.length) {
    return (
      <Card classNames={{
        base: 'w-full border-2 border-gray-200 shadow-none rounded-none',
      }}
      >
        <CardBody className="flex h-64 items-center justify-center">
          <h1 className={`
            text-xl font-semibold tracking-tighter text-gray-400
          `}
          >
            No issue trends data yet.
          </h1>
        </CardBody>
      </Card>
    )
  }

  return (
    <Card classNames={{
      base: 'w-full border-2 border-gray-200 shadow-none rounded-none',
    }}
    >
      <CardHeader className="flex items-center justify-between border-b border-gray-200">
        <h4 className="font-taurus text-lg font-semibold">Issues Over Time</h4>
        <div className="flex items-center gap-4">
          <TrendingUp size={20} className="text-gray-400" />
          <Select
            selectedKeys={[timeInterval]}
            onSelectionChange={keys => setTimeInterval(Array.from(keys)[0])}
            className="w-32"
            size="sm"
            variant="bordered"
            radius="none"
            classNames={{
              trigger: 'border-gray-200',
              value: 'font-mono text-sm',
            }}
          >
            <SelectItem key="hourly">Hourly</SelectItem>
            <SelectItem key="daily">Daily</SelectItem>
            <SelectItem key="weekly">Weekly</SelectItem>
          </Select>
        </div>
      </CardHeader>
      <CardBody>
        <ResponsiveContainer width="100%" height={250}>
          <AreaChart
            data={chartData}
            margin={{
              top: 10,
              right: 30,
              left: 0,
              bottom: 0,
            }}
          >
            <defs>
              <linearGradient id="gradient-low" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={SEVERITY_COLORS.low} stopOpacity={0.3} />
                <stop offset="95%" stopColor={SEVERITY_COLORS.low} stopOpacity={0} />
              </linearGradient>
              <linearGradient id="gradient-medium" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={SEVERITY_COLORS.medium} stopOpacity={0.3} />
                <stop offset="95%" stopColor={SEVERITY_COLORS.medium} stopOpacity={0} />
              </linearGradient>
              <linearGradient id="gradient-high" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={SEVERITY_COLORS.high} stopOpacity={0.3} />
                <stop offset="95%" stopColor={SEVERITY_COLORS.high} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
            <XAxis
              dataKey="formattedDate"
              tick={{ fontSize: 12, fontFamily: 'ui-monospace, monospace' }}
              interval="preserveStartEnd"
            />
            <YAxis
              tick={{ fontSize: 12, fontFamily: 'ui-monospace, monospace' }}
            />
            <Tooltip
              content={({ active, payload, label }) => {
                if (active && payload && payload.length) {
                  const data = payload[0].payload
                  return (
                    <div className={`
                      border-2 border-gray-200 bg-white p-3 shadow-none
                    `}
                    >
                      <p className="mb-2 font-mono text-sm font-medium">{label}</p>
                      <div className="flex flex-col gap-1 font-mono text-xs">
                        <div>
                          Total Issues:
                          {' '}
                          {data.total_issues}
                        </div>
                        <div>
                          Calls with Issues:
                          {' '}
                          {data.calls_with_issues}
                        </div>
                        <div className="mt-1 flex flex-col gap-1">
                          {data.high_severity > 0 && (
                            <div className="flex items-center gap-2">
                              <div
                                className="h-2 w-2 rounded-full"
                                style={{ backgroundColor: SEVERITY_COLORS.high }}
                              />
                              <span>
                                High:
                                {' '}
                                {data.high_severity}
                              </span>
                            </div>
                          )}
                          {data.medium_severity > 0 && (
                            <div className="flex items-center gap-2">
                              <div
                                className="h-2 w-2 rounded-full"
                                style={{ backgroundColor: SEVERITY_COLORS.medium }}
                              />
                              <span>
                                Medium:
                                {' '}
                                {data.medium_severity}
                              </span>
                            </div>
                          )}
                          {data.low_severity > 0 && (
                            <div className="flex items-center gap-2">
                              <div
                                className="h-2 w-2 rounded-full"
                                style={{ backgroundColor: SEVERITY_COLORS.low }}
                              />
                              <span>
                                Low:
                                {' '}
                                {data.low_severity}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )
                }
                return null
              }}
            />
            <Legend
              wrapperStyle={{
                paddingTop: '20px',
                fontSize: '12px',
                fontFamily: 'ui-monospace, monospace',
              }}
            />
            <Area
              type="monotone"
              dataKey="low_severity"
              stackId="1"
              stroke={SEVERITY_COLORS.low}
              fill="url(#gradient-low)"
              name="Low"
            />
            <Area
              type="monotone"
              dataKey="medium_severity"
              stackId="1"
              stroke={SEVERITY_COLORS.medium}
              fill="url(#gradient-medium)"
              name="Medium"
            />
            <Area
              type="monotone"
              dataKey="high_severity"
              stackId="1"
              stroke={SEVERITY_COLORS.high}
              fill="url(#gradient-high)"
              name="High"
            />
          </AreaChart>
        </ResponsiveContainer>
      </CardBody>
    </Card>
  )
}

export default function IssuesAnalytics({ schemaId, dateRange }) {
  const { data, isLoading } = useIssuesAnalytics(schemaId, dateRange)

  return (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <IssuesSummary data={data} isLoading={isLoading} />
        <IssuesByType data={data} isLoading={isLoading} />
      </div>
      <IssuesTimeline schemaId={schemaId} dateRange={dateRange} />
    </div>
  )
}